import { ActionConfirmation, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { formatDuration } from "../format.js";
import {
  addStaffPracticeTodo,
  getStaffPracticeTodos,
  withdrawStaffPracticeTodo,
  type PracticeTodo,
} from "../practiceTodos.js";
import { getStaffRepertoire } from "../repertoire.js";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";
import { emptyPracticeTaskDraft, type PracticeTaskDraft } from "../practiceTaskDraft.js";

export function PracticeTodoPanel({
  studentId,
  studentName,
  onBusyChange,
  draft,
  onDraftChange,
}: {
  studentId: string;
  studentName: string;
  onBusyChange: (busy: boolean) => void;
  draft?: PracticeTaskDraft;
  onDraftChange?: (draft: PracticeTaskDraft) => void;
}) {
  const todos = useAsync(() => getStaffPracticeTodos(studentId), [studentId]);
  const repertoire = useAsync(() => getStaffRepertoire(studentId), [studentId]);
  const [localDraft, setLocalDraft] = useState(emptyPracticeTaskDraft);
  const currentDraft = draft ?? localDraft;
  const { title, description, repertoireId, suggestedMinutes } = currentDraft;
  const changeDraft = onDraftChange ?? setLocalDraft;
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<ActionOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [withdrawing, setWithdrawing] = useState<PracticeTodo | null>(null);

  function setOperationBusy(next: boolean) {
    setBusy(next);
    onBusyChange(next);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || title.trim() === "") return;
    setOperationBusy(true);
    setProblem(null);
    setSaved(null);
    try {
      await addStaffPracticeTodo(studentId, {
        title,
        description: description.trim() === "" ? null : description,
        repertoireId: repertoireId || null,
        suggestedMinutes: suggestedMinutes === "" ? null : Number(suggestedMinutes),
      });
      changeDraft(emptyPracticeTaskDraft());
      setSaved({ variant: "success", title: "Practice to-do added", description: studentName, details: [{ value: title }] });
      todos.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not add that practice task.");
    } finally {
      setOperationBusy(false);
    }
  }

  async function withdraw() {
    if (withdrawing === null || busy) return;
    setOperationBusy(true);
    setProblem(null);
    setSaved(null);
    try {
      await withdrawStaffPracticeTodo(studentId, withdrawing.id);
      setSaved({ variant: "success", title: "Practice to-do withdrawn", description: studentName, details: [{ value: withdrawing.title }] });
      setWithdrawing(null);
      todos.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not withdraw that practice task.");
    } finally {
      setOperationBusy(false);
    }
  }

  return (
    <div className="stack">
      <div>
        <h3 style={{ marginTop: 0 }}>Set practice to-do</h3>
        <p className="small muted">
          This appears on {studentName}&rsquo;s Practice page. They can practise it with the timer,
          tick it off when it is done, and the time they spend on it appears on their student record.
        </p>
      </div>

      <form onSubmit={submit} className="stack">
        <label>
          <span className="small">What should they practise?</span>
          <input
            type="text"
            value={title}
            onChange={(event) => changeDraft({ ...currentDraft, title: event.target.value })}
            maxLength={200}
            required
            autoFocus
            style={{ width: "100%" }}
            placeholder="e.g. Bars 1–8 slowly, hands separately"
          />
        </label>
        <label>
          <span className="small">Suggested daily practice (optional)</span>
          <input
            type="number"
            min={1}
            max={480}
            value={suggestedMinutes}
            onChange={(event) => changeDraft({ ...currentDraft, suggestedMinutes: event.target.value })}
            style={{ width: "100%" }}
            placeholder="e.g. 10"
          />
          <span className="small muted" style={{ display: "block", marginTop: 3 }}>
            A guide for each practice day, not a countdown or a limit.
          </span>
        </label>
        <label>
          <span className="small">Repertoire (optional)</span>
          <select
            value={repertoireId}
            onChange={(event) => changeDraft({ ...currentDraft, repertoireId: event.target.value })}
            disabled={repertoire.loading && !repertoire.data}
            style={{ width: "100%" }}
          >
            <option value="">No repertoire link</option>
            {(repertoire.data?.current ?? []).map((entry) => (
              <option value={entry.id} key={entry.id}>
                {entry.composer ? `${entry.composer} — ` : ""}{entry.title}
              </option>
            ))}
          </select>
          {repertoire.error && (
            <span className="small muted" style={{ display: "block", marginTop: 3 }}>
              Repertoire is unavailable right now; the task can still be added without a link.
            </span>
          )}
        </label>
        <label>
          <span className="small">Extra detail (optional)</span>
          <textarea
            value={description}
            onChange={(event) => changeDraft({ ...currentDraft, description: event.target.value })}
            maxLength={2000}
            rows={3}
            style={{ width: "100%" }}
            placeholder="Tempo, repetitions, fingering, or anything else useful"
          />
        </label>
        {problem && <div className="error small">{problem}</div>}
        {saved && <ActionConfirmation {...saved} />}
        <div className="row">
          <button type="submit" disabled={busy || title.trim() === ""}>
            {busy ? "Adding…" : "Add to practice"}
          </button>
        </div>
      </form>

      <div>
        <h4 style={{ marginBottom: 8 }}>Practice to-dos</h4>
        {todos.loading && !todos.data && <p className="small muted">Loading practice to-dos…</p>}
        {todos.error && <div className="error small">{todos.error}</div>}
        {todos.data && todos.data.length === 0 && (
          <p className="small muted">No practice to-dos have been set yet.</p>
        )}
        {todos.data && todos.data.length > 0 && (
          <div className="stack" style={{ gap: 8 }}>
            {todos.data.map((todo) => {
              const practiceSeconds = Number.isFinite(todo.practiceSeconds) ? todo.practiceSeconds : 0;
              return (
                <div className="notice small" key={todo.id}>
                  <div className="spread" style={{ gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                      <strong className={todo.withdrawnAt ? "muted" : undefined}>{todo.title}</strong>
                      {(todo.suggestedMinutes != null || practiceSeconds > 0) && (
                        <div className="muted" style={{ marginTop: 4 }}>
                          {todo.suggestedMinutes != null && <>Aim for {todo.suggestedMinutes} min/day</>}
                          {todo.suggestedMinutes != null && practiceSeconds > 0 && " · "}
                          {practiceSeconds > 0 && <>Practised {formatDuration(practiceSeconds)}</>}
                        </div>
                      )}
                      {todo.repertoireTitle && (
                        <div className="muted" style={{ marginTop: 4 }}>
                          Repertoire: {todo.repertoireComposer ? `${todo.repertoireComposer} — ` : ""}
                          {todo.repertoireTitle}
                        </div>
                      )}
                      {todo.description && <div className="muted" style={{ marginTop: 4 }}>{todo.description}</div>}
                    </div>
                    <div className="row" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <span className={`tag${todo.withdrawnAt ? "" : todo.completed ? "" : " warn"}`}>
                        {todo.withdrawnAt ? "No longer assigned" : todo.completed ? "Done" : "To do"}
                      </span>
                      {!todo.withdrawnAt && (
                        <button
                          className="quiet small"
                          type="button"
                          disabled={busy}
                          onClick={() => setWithdrawing(todo)}
                        >
                          Withdraw
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {withdrawing && (
        <ConfirmDialog
          title="Withdraw this practice to-do?"
          description={`It will be removed from ${studentName}'s current practice. Completed work, notes and practice history will be kept.`}
          confirmLabel="Withdraw to-do"
          confirmingLabel="Withdrawing…"
          confirmClassName="action-danger"
          busy={busy}
          onConfirm={withdraw}
          onClose={() => setWithdrawing(null)}
        >
          <p><strong>{withdrawing.title}</strong></p>
        </ConfirmDialog>
      )}
    </div>
  );
}
